import React from 'react';
import styled from 'styled-components/macro';
import cleanDeep from 'clean-deep';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Formik, Field } from 'formik';
import { FieldCustom, FieldFile, FieldDate } from 'components/molecules';
import { Form, Button, Text } from 'components/atoms';
import { Error } from 'components/organisms'
import { toast } from 'react-toastify';
import { Toast } from 'containers';
import { useQuery, useMutation, queryCache } from 'react-query'
import { putFile, getFileById, FILES_KEY } from 'services/requests'

const FILE_SIZE_MB = 20;

export const FileSchema = Yup.object().shape({
  file: Yup.mixed()
    .notRequired()
    .nullable()
    .test(
      'fileSize',
      `Ficheiro demasiado grande, máximo de ${FILE_SIZE_MB}MB`,
      value => {
        const size = value ? value.size : 0;
        return size <= FILE_SIZE_MB * 1024 * 1024;
      }
    )
    .test(
      'fileFormat',
      'Formato inválido, para carregar imagens utilize a galeria.',
      value => (value && value.type ? value.type.indexOf('image') === -1 : true)
    )
    .test(
      'fileFormat',
      'O formato .exe não é válido',
      value => (value && value.type ? value.type.indexOf('.exe') === -1 : true)
    ),
  title: Yup.string()
    .max(255, 'Máximo de 255 caracteres')
    .required('Campo obrigatório'),
  description: Yup.string()
    .max(255, 'Máximo de 255 caracteres')
    .notRequired()
    .nullable(),
  expiresAt: Yup.string().notRequired().nullable()
});

const FileUpdateForm = ({
  subweb,
  fileId,
  lang,
  onSubmit,
  onDiscard,
}) => {
  const {
    isLoading: isLoadingFile,
    isError,
    data
  } = useQuery([FILES_KEY, { fileId }], getFileById)

  const [updateFile, { isLoading }] = useMutation(putFile, {
    onSuccess: (data) => {
      queryCache.invalidateQueries(FILES_KEY)
      onSubmit(data.data)
      toast.success(
        <Toast
          type="success"
          title="Ficheiro atualizado"
          description="O ficheiro foi atualizado com sucesso."
        />
      );
    },
    onError: (err) => {
      toast.error(
        <Toast
          type="error"
          title="Erro a atualizar ficheiro"
          description={err.message}
        />
      );
    }
  })

  const file = React.useMemo(() => {
    if (data && data.data) {
      return data.data
    }
    return null
  }, [data])

  const handleSubmit = async values => {
    const {
      file,
      title,
      description,
      date,
    } = values;

    const updatedFile = cleanDeep({
      file,
      title,
      description,
      expiresAt: date,
    })

    updateFile({
      fileId,
      subweb,
      language: lang,
      ...updatedFile,
    })
  };

  if (isError) {
    return (
      <Error message="Erro a carregar dados do ficheiro" />
    )
  }

  if (isLoadingFile) {
    return (
      <Text>A carregar dados do ficheiro...</Text>
    )
  }

  return (
    <Formik
      initialValues={file}
      validationSchema={FileSchema}
      onSubmit={(values, { setSubmitting }) => {
        handleSubmit(values);
        setSubmitting(false);
      }}
      enableReinitialize
    >
      {({ isSubmitting, dirty }) => (
        <Form>
          <Field
            name="file"
            title="Ficheiro"
            type="file"
            component={FieldFile}
            accept="video/*,audio/*,application/*,text/*"
            initialName={file.orginalName}
            buttonText="substituir ficheiro"
          />
          <Field
            name="title"
            title="Título"
            component={FieldCustom}
            maxLetters={255}
            info="título do ficheiro"
          />
          <Field
            name="description"
            title="Descrição"
            component={FieldCustom}
            maxLetters={255}
            info="descrição do ficheiro"
          />
          <Field
            name="date"
            title="Data de expiração"
            type="date"
            component={FieldDate}
          />
          <Actions>
            <Button
              type="button"
              modifiers={['marginRight', 'capitalize']}
              onClick={onDiscard}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              modifiers={['dark', 'capitalize', dirty ? '' : 'disabled']}
              loading={isSubmitting || isLoading}
            >
              Editar
            </Button>
          </Actions>
        </Form>
      )}
    </Formik>
  );
};

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: ${props => props.theme.spacing.l};
`

FileUpdateForm.propTypes = {
  onSubmit: PropTypes.func,
  onDiscard: PropTypes.func,
  lang: PropTypes.string,
  subweb: PropTypes.string.isRequired,
  fileId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired

};

FileUpdateForm.defaultProps = {
  onSubmit: () => null,
  onDiscard: () => null,
  lang: 'pt',
};

export default FileUpdateForm;
